import type { ReactElement } from "react";
import type { TicketKarte } from "@kundeninfo/api-client";
import { Icon, useSofortPopup } from "@kundeninfo/ui";
import { Avatar as MeAvatar } from "@kundeninfo/ui/me-design";
import { Avatar, useOhneTipp } from "./Avatar.js";
import { HIERARCHY_TEXT } from "./hierarchyTexts.js";

/**
 * DER EIGENTÜMER (Spec Teilaufgaben R7, R20, R24, W2): Person wie Zuständig (F48), Team als `me-avatar` mit seinem
 * Kürzel, „System" mit dem Symbol `server` (bis W2 kein Avatar dafür). Eine Stelle für Steckbrief, Tabelle, Randliste.
 * `ohneTipp`: der Kreis zeigt kein eigenes Sofort-Popup — für Zellen, die um das Zeichen das ihre tragen (Festlegung 23).
 * Ohne sichtbaren Namen hat jedes Zeichen einen Tabulator-Halt, damit sein Popup per Tastatur erreichbar ist (wie `Avatar`).
 * Fehlt Person oder Team (der Server liefert das nie), steht der leere Kreis — nie „System“.
 */
export function OwnerBadge(p: { owner: TicketKarte["owner"]; ichId: string; mitName?: boolean; ohneTipp?: boolean }): ReactElement {
  const mitName = p.mitName === true;
  const ohneTipp = useOhneTipp(p.ohneTipp);
  const popup = useSofortPopup<HTMLSpanElement>(p.owner.kind === "SYSTEM" && p.ohneTipp !== true
    ? HIERARCHY_TEXT.ownerOf(HIERARCHY_TEXT.system) : "");
  const ohneNameTipp = { ...(mitName ? {} : { ohneName: true }), ...(p.ohneTipp === true ? { ohneTipp: true } : {}) };
  if (p.owner.kind === "USER" || (p.owner.kind === "TEAM" && p.owner.team === null)) {
    const u = p.owner.user;
    return <Avatar person={u} ich={u?.id === p.ichId} {...ohneNameTipp} />;
  }
  if (p.owner.kind === "TEAM" && p.owner.team !== null) {
    const t = p.owner.team;
    return <MeAvatar ref={ohneTipp} name={t.name} kuerzel={t.shortCode} nutzerId={`team:${t.id}`} mitName={mitName} tabhalt={!mitName} />;
  }
  return (
    <span ref={popup} className="ki-system" role="img" aria-label={HIERARCHY_TEXT.ownerOf(HIERARCHY_TEXT.system)}
      {...(mitName ? {} : { tabIndex: 0 })}>
      <Icon name="server" size={16} />{mitName && <span className="ki-system__name">{HIERARCHY_TEXT.system}</span>}
    </span>
  );
}

/** R24, Festlegung 23: das Team als Warteschlange auf der Karte — der Name des Avatars sagt es. Ohne sichtbaren Namen mit
 *  eigenem Tabulator-Halt, damit das Popup auch per Tastatur erreichbar ist (wie `Avatar` mit `ohneName`). */
export function QueueBadge(p: { team: { id: string; shortCode: string; name: string } }): ReactElement {
  return <MeAvatar name={HIERARCHY_TEXT.queueOf(p.team.name)} kuerzel={p.team.shortCode} nutzerId={`team:${p.team.id}`}
    tabhalt />;
}
